<template>
  <v-dialog
    v-model="localDialog"
    :max-width="modalLayout.MD_WIDE"
    v-bind="modalP2FormViewport"
    @close="close"
    @keydown.esc="close"
    >
    <v-form ref="companyManagerForm" v-model="valid" @submit.prevent="save">
      <v-card>
        <CrudFormDialogHeader
          :kicker="$t('companyManagerCrud.title')"
          :title="formTitle"
          show-close
          @close="close"
        />
        <v-card-text style="padding-top: 20px; font-size: 14px">
          <v-row>
            <v-col cols="12">
              <v-card flat class="ml-5">
                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('companyManagerCrud.col.name') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-text-field
                      v-model="editedItem.name"
                      :rules="[requiredRule]"
                      required
                      dense
                      hide-details="auto"
                      :data-testid="getTestId('manager-name-input')"
                    />
                  </v-col>
                </v-row>

                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding">
                    <label>{{ $t('companyManagerCrud.col.phone') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-text-field
                      v-model="editedItem.phone"
                      dense
                      hide-details="auto"
                      :data-testid="getTestId('manager-phone-input')"
                    />
                  </v-col>
                </v-row>

                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding">
                    <label>{{ $t('companyManagerCrud.col.email') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-text-field
                      v-model="editedItem.email"
                      dense
                      hide-details="auto"
                      :data-testid="getTestId('manager-email-input')"
                    />
                  </v-col>
                </v-row>

                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding required">
                    <label>{{ $t('companyManagerCrud.col.companyDisplay') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-combobox
                      :value="getSelectedCompanyForCombobox()"
                      :items="companies"
                      item-text="companyName"
                      item-value="id"
                      :placeholder="$t('companyManagerCrud.dialog.companyPlaceholder')"
                      dense
                      hide-details="auto"
                      :loading="companiesLoading"
                      :disabled="companiesLoading"
                      :data-testid="getTestId('manager-company-select')"
                      return-object
                      clearable
                      @change="onCompanyChange"
                    />
                  </v-col>
                </v-row>

                <v-row align="baseline">
                  <v-col cols="3" class="custom-padding">
                    <label>{{ $t('companyManagerCrud.col.siteRelationsDisplay') }}</label>
                  </v-col>
                  <v-col cols="9" class="custom-padding">
                    <v-combobox
                      :value="getSelectedSitesForCombobox()"
                      :items="sites"
                      item-text="name"
                      item-value="id"
                      multiple
                      chips
                      deletable-chips
                      :placeholder="$t('companyManagerCrud.dialog.sitesPlaceholder')"
                      dense
                      hide-details="auto"
                      :loading="sitesLoading"
                      :disabled="sitesLoading"
                      :data-testid="getTestId('manager-sites-select')"
                      return-object
                      clearable
                      @change="onSitesChange"
                    >
                      <template slot="selection" slot-scope="{ item, index }">
                        <v-chip
                          v-if="index < 3"
                          small
                          color="primary"
                          text-color="text"
                        >
                          {{ item.name }}
                        </v-chip>
                        <span v-else-if="index === 3" class="secondary--text text-caption ml-2">
                          {{ $t('companyManagerCrud.dialog.moreChip', { count: editedItem.siteRelations.length - 3 }) }}
                        </span>
                      </template>
                    </v-combobox>

                    <div class="text-caption secondary--text mt-1">
                      {{ $t('companyManagerCrud.dialog.selectedSitesPrefix') }} {{ selectedSitesText }}
                    </div>

                    <!-- 🆕 현재 담당 사이트 표시 (수정 모드에서만) -->
                    <div v-if="isEdit && currentManagerSites.length > 0" class="mt-2">
                      <v-card outlined class="pa-2">
                        <div class="text-caption font-weight-medium mb-1">
                          {{ $t('companyManagerCrud.dialog.currentSitesTitle') }}
                        </div>
                        <div class="d-flex flex-wrap">
                          <v-chip
                            v-for="relation in currentManagerSites"
                            :key="relation.siteSeq"
                            small
                            color="secondary"
                            text-color="text"
                            outlined
                            class="ma-1"
                          >
                            {{ relation.siteName }}
                          </v-chip>
                        </div>
                      </v-card>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn outlined class="solvit-crud-outline-btn" @click="close">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            outlined
            class="solvit-crud-outline-btn solvit-crud-outline-btn--primary"
            type="submit"
            :data-testid="getTestId('manager-save-button')"
          >
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import Api from '../../apis/Api';
import { getTestId } from '@/utils/testUtils';
import { getValueOrDefault, getObjectPropertyOrDefault, joinArrayOrDefault } from '@/utils/tableUtils';
import CrudFormDialogHeader from '@/components/common/CrudFormDialogHeader.vue';
import { MODAL_MAX_WIDTH, MODAL_P2_FORM_VIEWPORT } from '@/constants/modalLayout';

export default {
  name: 'CompanyManagerEditDialog',
  components: {
    CrudFormDialogHeader
  },
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      default: () => ({
        id: null,
        name: '',
        phone: '',
        email: '',
        company: null, // 🆕 소속 회사 추가
        siteRelations: [] // 🆕 siteSeqs → siteRelations로 변경
      })
    },
    sites: {
      type: Array,
      default: () => []
    },
    companies: { // 🆕 회사 목록 추가
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    modalLayout: MODAL_MAX_WIDTH,
    modalP2FormViewport: MODAL_P2_FORM_VIEWPORT,
    valid: true,
    editedItem: {
      id: null, // 🆕 seq → id로 변경
      name: '',
      phone: '',
      email: '',
      company: null, // 🆕 소속 회사 추가
      siteRelations: [] // 🆕 selectedSites → siteRelations로 변경
    },
    defaultItem: {
      id: null,
      name: '',
      phone: '',
      email: '',
      company: null, // 🆕 소속 회사 추가
      siteRelations: []
    },
    sitesLoading: false,
    companiesLoading: false // 🆕 회사 목록 로딩 상태 추가
  }),
  computed: {
    localDialog: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('update:dialog', value);
      }
    },
    formTitle() {
      return this.editedItem?.id
        ? this.$t('companyManagerCrud.dialog.editTitle')
        : this.$t('companyManagerCrud.dialog.addTitle');
    },
    requiredRule() {
      return (v) => !!v || this.$t('common.required');
    },
    selectedSitesText() {
      return joinArrayOrDefault(
        this.editedItem.siteRelations,
        relation => relation.siteName,
        ', ',
        '-'
      );
    },
    isEdit() {
      return !!this.editedItem?.id; // 🆕 seq → id로 변경
    },
    currentManagerSites() {
      return this.editedItem?.siteRelations ?? [];
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        this.initialize();
      } else {
        console.log('다이얼로그 닫힘');
      }
    },
    item: {
      immediate: true,
      handler(newVal) {
        this.editedItem = Object.assign({}, newVal);
        console.log('this.editedItem:', newVal);
      }
    }
  },
  methods: {
    getTestId(testId) {
      return getTestId(testId);
    },

    // 🆕 v-combobox에 표시할 사이트 목록 반환
    getSelectedSitesForCombobox() {
      if (!this.editedItem.siteRelations || !Array.isArray(this.editedItem.siteRelations)) {
        return [];
      }

      // siteRelations에서 Site 객체 형태로 변환하여 반환
      return this.editedItem.siteRelations
        .filter(relation => relation.siteSeq && relation.siteName)
        .map(relation => ({
          id: relation.siteSeq,
          name: relation.siteName
        }));
    },

    // 🆕 v-combobox에 표시할 회사 정보 반환
    getSelectedCompanyForCombobox() {
      console.log('this.editedItem.company:', this.editedItem.company);
      if (!this.editedItem.company || !this.editedItem.company.id) {
        return null;
      }

      // 현재 선택된 회사 정보 반환
      return {
        id: this.editedItem.company.id,
        companyName: this.editedItem.company.companyName
      };
    },

    // 🆕 사이트 선택 변경 시 CompanyManagerSiteRelationPublic 형태로 변환
    onSitesChange(selectedSites) {
      console.log('사이트 선택 변경:', selectedSites);

      if (selectedSites && Array.isArray(selectedSites)) {
        // 중복 제거를 위해 Map 사용
        const siteMap = new Map();

        selectedSites.forEach(site => {
          if (site && site.id && site.name) {
            // 🆕 기존 siteRelations에서 responsibility 정보 유지
            const existingRelation = this.editedItem.siteRelations.find(
              relation => relation.siteSeq === site.id
            );

            siteMap.set(site.id, {
              siteSeq: site.id,
              siteName: site.name,
              responsibility: existingRelation?.responsibility || '' // 기존 값 유지 또는 기본값
            });
          }
        });

        // Map의 값들을 배열로 변환
        this.editedItem.siteRelations = Array.from(siteMap.values());
        console.log('변환된 siteRelations:', this.editedItem.siteRelations);
      } else {
        this.editedItem.siteRelations = [];
      }
    },

    // 🆕 회사 선택 변경 시 처리
    onCompanyChange(selectedCompany) {
      console.log('회사 선택 변경:', selectedCompany);

      if (selectedCompany && selectedCompany.id) {
        // 선택된 회사 정보 설정
        this.editedItem.company = {
          id: selectedCompany.id,
          companyName: selectedCompany.companyName || selectedCompany.name
        };
        console.log('설정된 회사 정보:', this.editedItem.company);
      } else {
        // 회사 선택 해제
        this.editedItem.company = null;
        console.log('회사 선택 해제됨');
      }
    },

    initialize() {
      if (!this.item?.id) {
        // 추가 모드
        this.editedItem = { ...this.defaultItem };
        this.editedItem.siteRelations = [];
      } else {
        // 수정 모드
        this.editedItem = {
          id: this.item.id, // 🆕 seq → id로 변경
          name: this.item.name ?? '',
          phone: this.item.phone ?? '',
          email: this.item.email ?? '',
          company: this.item.company ?? null, // 🆕 회사 정보 설정
          siteRelations: [] // 🆕 selectedSites → siteRelations로 변경
        };

        // 🆕 현재 담당 사이트 설정 (서버에서 받은 형태 그대로 사용)
        if (this.item.siteRelations?.length > 0) {
          this.editedItem.siteRelations = this.item.siteRelations;
        }
      }
    },

    validateForm() {
      this.$refs.companyManagerForm.validate();
      return this.valid;
    },

    close() {
      this.$emit('close');
      this.$nextTick(() => {
        if (this.$refs.companyManagerForm) {
          this.$refs.companyManagerForm.resetValidation();
        }
        this.editedItem = {
          name: '',
          phone: '',
          email: '',
          siteRelations: [] // 🆕 selectedSites → siteRelations로 변경
        };
      });
    },

    async save() {
      if (!this.$refs.companyManagerForm?.validate()) return;

      try {
        // 🆕 백엔드 API에 맞는 데이터 구조로 변환
        const requestData = {
          id: this.editedItem.id ?? '',
          name: this.editedItem.name ?? '',
          phone: this.editedItem.phone ?? '',
          email: this.editedItem.email ?? '',
          companySeq: this.editedItem.company?.id ?? null, // 🆕 회사 ID 설정
          siteRelations: this.editedItem.siteRelations ?? [] // 🆕 서버에서 받은 형태 그대로 사용
        };
        console.log('this.editedItem.selectedSites:', this.editedItem);
        console.log('저장할 데이터:', requestData);

        if (this.editedItem?.id) {
          // 🆕 수정 API 호출
          await Api.update(`/company-managers/${this.editedItem?.id}`, requestData,true);
        } else {
          // 🆕 추가 API 호출
          await Api.create('/company-managers', requestData);
        }

        this.$emit('success');
        this.close();
      } catch (error) {
        console.error('담당자 저장 실패:', error);
        alert(this.$t('companyManagerCrud.dialog.saveError'));
      }
    }
  }
};
</script>

<style scoped>
.v-dialog__content {
  max-height: 100%;
}
.v-dialog:not(.v-dialog--fullscreen) {
  max-height: 100%;
}
.custom-padding {
  padding: 8px 12px !important;
}

.required::after {
  content: "*";
  color: red;
  margin-left: 4px;
}
</style>
